import {Accordion, AccordionDetails, AccordionSummary, Card, Grid, Typography} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef} from 'react';
import {PathValue, useFormContext} from 'react-hook-form';
import {useApplicationData} from '@/Core/hooks/useApplicationData';
import {User} from '../../../Auth/User';
import {CategoryHeader} from '../../../Core/UI';
import {Parameter, ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/PrequalXref';
import {Quote} from '../../../Core/mobx/ParameterStore/Quote';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {FormType} from '../../PreQualification/types';
import Basic from '../../Questions/Basic';
import {DISTRIBUTION_END_AGE, DISTRIBUTION_START_AGE, PROSPER_DEFAULT_RATE_OF_RETURN} from '../constants';
import {ProsperFormValues} from './lib/validation';
import FormField, {FormFieldType} from './ui/FormField';
import {shouldShowHHIWarning, WarningMessage} from '@/Agent/Common/WarningMessage';
import {AB_FLAGS} from '../../../Core/constants/abFlags';
import RootStore from '../../../Core/mobx/RootStore';

export interface ProsperTabProps {
    formType: FormType;
    parameters: Quote;
    parameterStore: ParameterStore;
    valueChanged: (p: Parameter, value: any, formType?: FormType) => void;
}

const ProsperTab = observer(({valueChanged, parameters, parameterStore, formType}: ProsperTabProps) => {
    const {
        formState: {errors, isValid},
        setValue,
        watch,
        trigger,
    } = useFormContext<ProsperFormValues>();

    const rawHHI = parameterStore.prequal.incomeHouseholdText.value ?? '';
    const hhi = Number(String(rawHHI).replace(/[,$]/g, '')) || 0;

    const isPremiumSpecify = parameters.prosperSpecify.value === 'Premium';
    const prosperAmount = Number(parameters.prosperAmount.value) || 0;

    // Get the AB flag value from the parameter store
    const lowerHouseholdIncomeLimit =
        RootStore.agentPortalStore.parameterStore.abFlags[AB_FLAGS.LOWER_HOUSEHOLD_INCOME_LIMIT] === true;

    // For Build a Quote > Amount: always use Monthly for threshold
    const {exceeds: showPremiumWarningAmount, threshold: premiumThresholdAmount} = shouldShowHHIWarning({
        premium: prosperAmount,
        annualHHI: hhi,
        premiumMode: 'Monthly',
        lowerHouseholdIncomeLimit,
    });
    // For Premium Schedule > Premium: use selected premiumMode
    const premiumMode = parameters.prosperPremiumMode?.value || 'Monthly';
    const premiumValue = Number(parameters.prosperPremium?.value) || 0;
    const {exceeds: showPremiumWarningPremium, threshold: premiumThresholdPremium} = shouldShowHHIWarning({
        premium: premiumValue,
        annualHHI: hhi,
        premiumMode,
        lowerHouseholdIncomeLimit,
    });

    const required = (
        <span>
            <span style={{color: 'red'}}>*</span>
        </span>
    );

    const {onValueChangedPreQual, onValueChanged, prequal} = parameterStore || {};
    const {applicationData} = useApplicationData();
    const hasInitializedDefaults = useRef(false);

    // The pre-qual "Tobacco/Marijuana Usage" answer is the default for the Prosper smoker field.
    const deriveSmokerFromPrequal = () => parameterStore.prequal.tobacco.value === true;

    // prosperSmoker holds an explicit value once seeded or manually changed; '' / undefined = not set yet.
    const smokerHasValue = () => parameters.prosperSmoker.value === true || parameters.prosperSmoker.value === false;

    const watchAmplifySolves = watch('prosperAmplifySolves');
    const watchProsperSpecify = watch('prosperSpecify');
    const watchAmplifyAmount = watch('prosperAmount');
    const watchDistributionAmount = watch('prosperDistributionAmount');
    const watchProsperFaceAmount = watch('prosperFaceAmount');

    const updateField = <K extends keyof ProsperFormValues>(
        fieldName: K,
        value: PathValue<ProsperFormValues, K>,
        valueChangedFn = onValueChanged,
    ) => {
        const param = parameters[fieldName as keyof typeof parameters];
        setValue(fieldName, value as any);
        valueChangedFn(null, param as Parameter, value);
    };

    useEffect(() => {
        updateField('prosperPremiumEndYear', 121 - Number(parameterStore.prequal.age.value));
    }, [parameterStore.prequal.age.value]);

    useEffect(() => {
        const age = Number(parameterStore.prequal.age.value) + Number(parameters.prosperPremiumEndYear.value);
        updateField('prosperPremiumAge', age);
    }, [parameters.prosperPremiumEndYear.value]);

    useEffect(() => {
        setValue('prosperSpecify', parameters.prosperSpecify.value);
    }, [parameters.prosperSpecify.value]);

    useEffect(() => {
        setValue('prosperAmount', Number(parameters.prosperAmount.value));
    }, [parameters.prosperAmount.value]);

    useEffect(() => {
        trigger(['prosperDeathBenefitChangeAge']);
    }, [parameters.prosperDeathBenefitOption.value]);

    useEffect(() => {
        trigger(['prosperSmoker']);
    }, [parameterStore.prequal.age.value]);

    useEffect(() => {
        trigger(['prosperAmount']);
    }, [parameters.prosperSpecify.value, parameters.prosperAmount.value]);

    useEffect(() => {
        // Pre-qual answer DEFAULTS the smoker field. Once it has a value (initial seed or a
        // manual change on this tab), it stays independent of the pre-qual answer.
        if (!smokerHasValue()) {
            updateField('prosperSmoker', deriveSmokerFromPrequal());
        }
        setValue('emailAddress', parameterStore.approval.emailAddress.value);
    }, [parameterStore.approval.emailAddress.value, parameterStore.prequal.tobacco.value]);

    useEffect(() => {
        if (!parameterStore.instantIulApplicationId || parameters.prosperSmoker.value !== deriveSmokerFromPrequal()) {
            if (parameters.prosperSmoker.value === true) {
                updateField('prosperRiskClass', 'StandardTobacco');
            } else {
                updateField('prosperRiskClass', 'StandardNonTobacco');
            }
        }
    }, [parameters.prosperSmoker.value, parameterStore.instantIulApplicationId]);

    useEffect(() => {
        trigger('prosperRiskClass');
    }, [parameterStore.prequal.tobacco.value]);

    useEffect(() => {
        // Automatically sync prosperSmoker based on selected prosperRiskClass
        const nonTobaccoClasses = ['SuperPreferredNonTobacco', 'PreferredNonTobacco', 'StandardNonTobacco'];
        const tobaccoClasses = ['PreferredTobacco', 'StandardTobacco'];

        // Mirror a manually-selected risk class onto the Smoker toggle only.
        // The Smoker field is independent of the pre-qual answer, so never write back to prequal.
        if (nonTobaccoClasses.includes(parameters.prosperRiskClass.value)) {
            if (parameters.prosperSmoker.value !== false) {
                updateField('prosperSmoker', false);
            }
            // Always trigger validation to ensure it passes
            trigger('prosperRiskClass');
        } else if (tobaccoClasses.includes(parameters.prosperRiskClass.value)) {
            if (parameters.prosperSmoker.value !== true) {
                updateField('prosperSmoker', true);
            }
            // Always trigger validation to ensure it passes
            trigger('prosperRiskClass');
        }
    }, [parameters.prosperRiskClass.value]);

    useEffect(() => {
        if (hasInitializedDefaults.current) return;
        // Wait for applicationData to load when there's a customer to fetch for,
        // so saved prosperData can seed the form instead of the hardcoded defaults.
        if (parameterStore.formid && applicationData === undefined) return;
        hasInitializedDefaults.current = true;

        const saved = applicationData?.prosperData;

        if (parameters.prosperAmplifySolves.value === '') {
            updateField('prosperAmplifySolves', saved?.prosperAmplifySolves || 'EasySolve');
        }
        if (parameters.prosperSpecify.value === '') {
            updateField('prosperSpecify', saved?.prosperSpecify || 'Premium');
        }
        if (parameters.prosperAmount.value === '') {
            const savedAmount = saved?.prosperAmount;
            const hasSavedAmount = savedAmount !== undefined && savedAmount !== null && savedAmount !== '';
            updateField('prosperAmount', hasSavedAmount ? savedAmount : parameterStore.prequal.monthlyBudget.value);
        }

        // On initial load, the pre-qual answer DEFAULTS smoker (only when not already set), and
        // risk class follows the smoker value. A manually-set smoker value is preserved on remount.
        // For an existing application, keep its saved risk class and just mirror it to the toggle.
        const tobaccoClasses = ['PreferredTobacco', 'StandardTobacco'];

        if (!parameterStore.instantIulApplicationId) {
            const isSmoker = smokerHasValue() ? parameters.prosperSmoker.value === true : deriveSmokerFromPrequal();
            updateField('prosperSmoker', isSmoker);
            updateField('prosperRiskClass', isSmoker ? 'StandardTobacco' : 'StandardNonTobacco');
        } else {
            updateField('prosperSmoker', tobaccoClasses.includes(parameters.prosperRiskClass.value));
        }

        // Trigger validation after sync
        setTimeout(() => {
            trigger('prosperRiskClass');
        }, 0);
    }, [applicationData]);

    useEffect(() => {
        trigger(['prosperFaceAmountType', 'prosperPremiumType']);
    }, [parameters.prosperPremiumType.value, parameters.prosperFaceAmountType.value]);

    useEffect(() => {
        trigger(['prosperFaceAmount', 'prosperPremium']);
    }, [parameters.prosperPremium.value, parameters.prosperFaceAmount.value]);

    useEffect(() => {
        trigger(['prosperDeathBenefitChangeAge']);
    }, [parameterStore.prequal.age.value, parameters.prosperDeathBenefitChangeAge.value]);

    useEffect(() => {
        if (parameters.prosperDeathBenefitOption.value === 'ALevel') {
            updateField('prosperDeathBenefitChangeAge', 0);
        }
    }, [parameterStore.prequal.age.value, parameters.prosperDeathBenefitOption.value]);

    useEffect(() => {
        trigger(['prosperDistributionStartAge']);
    }, [parameterStore.prequal.age.value, parameters.prosperDistributionStartAge.value]);

    useEffect(() => {
        trigger('prosperSmoker');
    }, [parameterStore.prequal.tobacco.value]);

    useEffect(() => {
        trigger('prosperDeathBenefitChangeAge');
    }, [parameters.prosperDeathBenefitOption.value]);

    useEffect(() => {
        if (watchDistributionAmount === 0) {
            updateField('prosperDistributionStartAge', DISTRIBUTION_START_AGE);
            updateField('prosperDistributionEndAge', DISTRIBUTION_END_AGE);
        }
        trigger(['prosperDeathBenefitChangeAge']);
    }, [watchDistributionAmount]);

    const handleDependenciesUpdate = (amplifySolves = watchAmplifySolves, prosperSpecify = watchProsperSpecify) => {
        switch (amplifySolves) {
            case 'OptimalSolve':
                updateField('prosperDeathBenefitOption', 'BIncreasing');
                updateField('prosperDeathBenefitChangeAge', 121);
                updateField('prosperPremiumEndYear', 121 - Number(parameterStore.prequal.age.value));
                updateField('prosperDefinitionOfLifeInsurance', 'CVAT');
                updateField('prosperPremiumMode', 'Monthly');
                updateField('prosperSolveAge', 121);
                updateField('prosperSpecify', 'Coverage');
                updateField('prosperFaceAmountType', 'Specify');
                updateField('prosperFaceAmount', watchAmplifyAmount!);
                updateField('prosperPremiumType', 'Solve');
                updateField('prosperPremium', 1);
                updateField('prosperDistributionAmount', 0);
                updateField('prosperSolveGoal', 1000);
                break;
            case 'SureSolve':
                updateField('prosperDeathBenefitOption', 'BIncreasing');
                updateField('prosperDeathBenefitChangeAge', 66);
                updateField('prosperPremiumEndYear', 121 - Number(parameterStore.prequal.age.value));
                updateField('prosperDefinitionOfLifeInsurance', 'CVAT');

                if (prosperSpecify === 'Premium') {
                    updateField('prosperFaceAmountType', 'Target');
                    updateField('prosperFaceAmount', 1);
                    updateField('prosperPremiumType', 'Specify');
                    updateField('prosperPremium', watchAmplifyAmount!);
                    updateField('prosperDistributionAmount', 0);
                    updateField('prosperSolveGoal', 1000);
                }
                if (prosperSpecify === 'Coverage') {
                    updateField('prosperFaceAmountType', 'Specify');
                    updateField('prosperFaceAmount', watchAmplifyAmount!);
                    updateField('prosperPremiumType', 'Target');
                    updateField('prosperPremium', 1);
                    updateField('prosperDistributionAmount', 0);
                    updateField('prosperSolveGoal', 1000);
                }
                break;
            case 'EasySolve':
                updateField('prosperDeathBenefitOption', 'BIncreasing');
                updateField('prosperDeathBenefitChangeAge', 66);
                updateField('prosperDefinitionOfLifeInsurance', 'CVAT');
                updateField('prosperPremiumMode', 'Monthly');
                updateField('prosperPremiumEndYear', 121 - Number(parameterStore.prequal.age.value));
                updateField('prosperPremiumAge', 121);
                updateField('prosperSolveAge', 121);
                updateField('prosperDistributionAmount', 0);

                if (prosperSpecify === 'Premium') {
                    updateField('prosperFaceAmountType', 'Solve');
                    updateField('prosperFaceAmount', 1);
                    updateField('prosperPremiumType', 'Specify');
                    updateField('prosperPremium', watchAmplifyAmount!);
                    updateField('prosperSolveGoal', 1);
                }
                if (prosperSpecify === 'Coverage') {
                    updateField('prosperFaceAmountType', 'Specify');
                    updateField('prosperFaceAmount', watchAmplifyAmount!);
                    updateField('prosperPremiumType', 'Solve');
                    updateField('prosperPremium', 1);
                    updateField('prosperSolveGoal', watchProsperFaceAmount!);
                }
                break;
            case 'OptimizeDeathBenefit':
                updateField('prosperDeathBenefitOption', 'ALevel');
                updateField('prosperDeathBenefitChangeAge', 0);
                updateField('prosperDefinitionOfLifeInsurance', 'CVAT');
                updateField('prosperPremiumEndYear', 100 - Number(parameterStore.prequal.age.value));

                if (prosperSpecify === 'Premium') {
                    updateField('prosperFaceAmountType', 'Solve');
                    updateField('prosperFaceAmount', 1);
                    updateField('prosperPremiumType', 'Specify');
                    updateField('prosperPremium', watchAmplifyAmount!);
                    updateField('prosperDistributionAmount', 0);
                    updateField('prosperSolveGoal', 1000);
                }

                if (prosperSpecify === 'Coverage') {
                    updateField('prosperFaceAmountType', 'Specify');
                    updateField('prosperFaceAmount', watchAmplifyAmount!);
                    updateField('prosperPremiumType', 'Solve');
                    updateField('prosperPremium', 1);
                    updateField('prosperDistributionAmount', 0);
                    updateField('prosperSolveGoal', 1000);
                }
                break;
            // TODO We may need to modify the input of prosperSolveGoal to accept and E for endow instead of using this value
            case 'OptimizeCashAccumulation':
                updateField('prosperDefinitionOfLifeInsurance', 'GPT');
                updateField('prosperDeathBenefitOption', 'BIncreasing');
                updateField('prosperDeathBenefitChangeAge', 66);
                updateField('prosperPremiumEndYear', 65 - Number(parameterStore.prequal.age.value));

                if (prosperSpecify === 'Premium') {
                    updateField('prosperFaceAmountType', 'Solve');
                    updateField('prosperFaceAmount', 1);
                    updateField('prosperPremiumType', 'Specify');
                    updateField('prosperPremium', watchAmplifyAmount!);
                    updateField('prosperDistributionAmount', 0);
                    updateField('prosperSolveGoal', watchProsperFaceAmount!);
                }

                if (prosperSpecify === 'Coverage') {
                    updateField('prosperFaceAmountType', 'Specify');
                    updateField('prosperFaceAmount', watchAmplifyAmount!);
                    updateField('prosperPremiumType', 'Solve');
                    updateField('prosperPremium', 1);
                    updateField('prosperDistributionAmount', 0);
                    updateField('prosperSolveGoal', watchProsperFaceAmount!);
                }
                break;
            case 'OptimizeIncome':
                updateField('prosperDefinitionOfLifeInsurance', 'GPT');
                updateField('prosperDeathBenefitOption', 'BIncreasing');
                updateField('prosperDeathBenefitChangeAge', 66);
                updateField('prosperPremiumEndYear', 65 - Number(parameterStore.prequal.age.value));
                updateField('prosperDistributionSolveType', 'Solve');

                if (prosperSpecify === 'Premium') {
                    updateField('prosperFaceAmountType', 'Guideline');
                    updateField('prosperFaceAmount', 1);
                    updateField('prosperPremiumType', 'Specify');
                    updateField('prosperPremium', watchAmplifyAmount!);
                    updateField('prosperDistributionAmount', 1000);
                    updateField('prosperSolveGoal', 1000);
                }

                if (prosperSpecify === 'Coverage') {
                    updateField('prosperFaceAmountType', 'Specify');
                    updateField('prosperFaceAmount', watchAmplifyAmount!);
                    updateField('prosperPremiumType', 'Guideline');
                    updateField('prosperPremium', 1);
                    updateField('prosperDistributionAmount', 1000);
                    updateField('prosperSolveGoal', 1000);
                    updateField('prosperSolveAge', 100);
                }
                break;
            default:
                break;
        }

        trigger(['prosperPremiumEndYear', 'prosperPremium', 'prosperPremiumType', 'prosperFaceAmount']);
    };

    useEffect(() => {
        if (!parameterStore.instantIulApplicationId) {
            updateField('prosperTableRating', 'NotRated');
            updateField('prosperFlatExtra', 0);
            updateField('prosperFlatExtraDuration', 'M');
            updateField('prosperDeathBenefitOption', 'BIncreasing');
            updateField('prosperDeathBenefitChangeAge', 66);
            updateField('prosperLumpSum', 0);
            updateField('prosperIndexedAccountRateOfReturn', PROSPER_DEFAULT_RATE_OF_RETURN);
            updateField('prosperDistributionType', '0'); // 'IndexedLoan'
            updateField('prosperAllowMEC', false);
            updateField('prosperDistributionStartAge', DISTRIBUTION_START_AGE);
            updateField('prosperDistributionEndAge', DISTRIBUTION_END_AGE);
            updateField('prosperIndexedAllocationPercent', 100);
            updateField('prosperDistributionSolveType', 'Specify');
            updateField('prosperSolveAge', 100);

            handleDependenciesUpdate();

            trigger([
                'prosperPremiumEndYear',
                'prosperPremium',
                'prosperIndexedAllocationPercent',
                'prosperPremiumType',
                'prosperDistributionType',
                'prosperFaceAmount',
            ]);
        }
    }, [watchAmplifySolves, watchProsperSpecify, parameterStore.instantIulApplicationId]);

    useEffect(() => {
        if (watchProsperSpecify === 'Premium' && parameters.prosperAmount.value !== parameters.prosperPremium.value) {
            updateField('prosperPremium', parameters.prosperAmount.value);
        } else if (
            watchProsperSpecify === 'Coverage' &&
            parameters.prosperAmount.value !== parameters.prosperFaceAmount.value
        ) {
            updateField('prosperFaceAmount', parameters.prosperAmount.value!);
        }
    }, [parameters.prosperAmount.value]);

    useEffect(() => {
        if (watchAmplifySolves === 'OptimizeCashAccumulation') {
            updateField('prosperSolveGoal', parameters.prosperFaceAmount.value);
        }
    }, [parameters.prosperFaceAmount.value]);

    useEffect(() => {
        if (watchProsperSpecify === 'Premium' && parameters.prosperAmount.value !== parameters.prosperPremium.value) {
            updateField('prosperAmount', parameters.prosperPremium.value!);
        } else if (
            watchProsperSpecify === 'Coverage' &&
            parameters.prosperAmount.value !== parameters.prosperFaceAmount.value
        ) {
            updateField('prosperAmount', parameters.prosperFaceAmount.value!);
        }
    }, [parameters.prosperPremium.value, parameters.prosperFaceAmount.value]);

    return (
        <>
            <Card
                style={{
                    padding: 20,
                    marginBottom: 10,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                }}
            >
                <Grid
                    item
                    style={{
                        marginBottom: 20,
                    }}
                    xs={12}
                >
                    <CategoryHeader name="Basic Information" number={1} />
                </Grid>
                <Grid container spacing={3}>
                    <Basic valueChanged={onValueChangedPreQual} quotes parameters={prequal} />
                    <Grid item xs={3}>
                        <SelectInputElement
                            param={{
                                label: 'State',
                                options: fieldOptions.state.options,
                                ...prequal.state,
                                name: 'state',
                            }}
                            required={required}
                            valueChanged={onValueChangedPreQual}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormField
                            name="emailAddress"
                            label="Email Address"
                            formFieldType={FormFieldType.TEXT}
                            parameter={parameterStore.approval.emailAddress}
                            errors={!!errors.emailAddress}
                            helperText={errors.emailAddress?.message}
                            valueChanged={(p, value, formType) => valueChanged(p, value?.trim(), formType)}
                            placeholder="example@gmail.com"
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <FormField
                            name="prosperSmoker"
                            label="Smoker"
                            formFieldType={FormFieldType.SWITCH}
                            parameter={parameters.prosperSmoker}
                            errors={!!errors.prosperSmoker}
                            helperText={errors.prosperSmoker?.message}
                            valueChanged={(p, value, formType) => {
                                // Manual change updates the quote's smoker value only. The pre-qual
                                // answer is just the default — a manual change here does NOT write
                                // back to pre-qual; from this point the two are independent.
                                valueChanged(p, value, formType);
                            }}
                            required
                        />
                        {smokerHasValue() && parameters.prosperSmoker.value !== deriveSmokerFromPrequal() && (
                            <Typography variant="caption" style={{color: '#6b7280', display: 'block', marginTop: 4}}>
                                Overridden
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <Grid item style={{marginTop: 20}} xs={12}>
                    <CategoryHeader name="Build A Quote" number={2} />
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={3}>
                        <FormField
                            name="prosperAmplifySolves"
                            label="Amplify Solves"
                            formFieldType={FormFieldType.SELECT}
                            options={[
                                'OptimalSolve',
                                'EasySolve',
                                'SureSolve',
                                'Optimize Cash Accumulation',
                                // 'Optimize Income',
                                'Optimize Death Benefit',
                            ]}
                            optionValues={[
                                'OptimalSolve',
                                'EasySolve',
                                'SureSolve',
                                'OptimizeCashAccumulation',
                                // 'OptimizeIncome',
                                'OptimizeDeathBenefit',
                            ]}
                            parameter={parameters.prosperAmplifySolves}
                            errors={!!errors.prosperAmplifySolves}
                            helperText={errors.prosperAmplifySolves?.message}
                            valueChanged={(p, v, f) => {
                                valueChanged(p, v, f);
                                if (parameterStore.instantIulApplicationId) {
                                    handleDependenciesUpdate(v, watchProsperSpecify);
                                }
                            }}
                            optionDisplayFormatter={(option) => {
                                if (option === 'EasySolve') {
                                    return 'EasySolve (For Testing)';
                                }
                                if (option === 'OptimalSolve') {
                                    return 'OptimalSolve (For Testing)';
                                }
                                return option;
                            }}
                            isOptionDisabledCb={(option) => option === 'SureSolve'}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormField
                            name="prosperSpecify"
                            label="Specify"
                            formFieldType={FormFieldType.SELECT}
                            disabled={watchAmplifySolves === 'OptimalSolve'}
                            options={['Premium', 'Coverage']}
                            optionValues={['Premium', 'Coverage']}
                            parameter={parameters.prosperSpecify}
                            errors={!!errors.prosperSpecify}
                            helperText={errors.prosperSpecify?.message}
                            valueChanged={(p, v, f) => {
                                valueChanged(p, v, f);
                                if (parameterStore.instantIulApplicationId) {
                                    handleDependenciesUpdate(watchAmplifySolves, v);
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormField
                            name="prosperAmount"
                            label="Amount"
                            formFieldType={FormFieldType.CURRENCY}
                            parameter={parameters.prosperAmount}
                            errors={!!errors.prosperAmount}
                            helperText={errors.prosperAmount?.message}
                            valueChanged={valueChanged}
                        />
                        {isPremiumSpecify && showPremiumWarningAmount && (
                            <WarningMessage value={premiumThresholdAmount} />
                        )}
                    </Grid>
                    <Grid item xs={3}>
                        <FormField
                            name="prosperIllustrationRequest"
                            label="Illustration"
                            formFieldType={FormFieldType.SWITCH}
                            parameter={parameters.prosperIllustrationRequest}
                            errors={!!errors.prosperIllustrationRequest}
                            helperText={errors.prosperIllustrationRequest?.message}
                            valueChanged={valueChanged}
                            required
                        />
                    </Grid>
                </Grid>
            </Card>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <CategoryHeader name="Advanced" error={!isValid} />
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <CategoryHeader name="Policyholder" number={3} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperRiskClass"
                                label="Risk Class"
                                formFieldType={FormFieldType.SELECT}
                                options={[
                                    'Preferred Tobacco',
                                    'Standard Tobacco',
                                    'Super Preferred Non-Tobacco',
                                    'Preferred Non-Tobacco',
                                    'Standard Non-Tobacco',
                                ]}
                                optionValues={[
                                    'PreferredTobacco',
                                    'StandardTobacco',
                                    'SuperPreferredNonTobacco',
                                    'PreferredNonTobacco',
                                    'StandardNonTobacco',
                                ]}
                                parameter={parameters.prosperRiskClass}
                                errors={!!errors.prosperRiskClass}
                                helperText={errors.prosperRiskClass?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>
                        {['StandardTobacco', 'StandardNonTobacco'].includes(parameters.prosperRiskClass.value) && (
                            <Grid item xs={4}>
                                <FormField
                                    name="prosperTableRating"
                                    label="Table Rating"
                                    formFieldType={FormFieldType.SELECT}
                                    options={['Not Rated', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']}
                                    optionValues={[
                                        'NotRated',
                                        'A',
                                        'B',
                                        'C',
                                        'D',
                                        'E',
                                        'F',
                                        'G',
                                        'H',
                                        'I',
                                        'J',
                                        'K',
                                        'L',
                                    ]}
                                    parameter={parameters.prosperTableRating}
                                    errors={!!errors.prosperTableRating}
                                    helperText={errors.prosperTableRating?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                        )}
                        <Grid item xs={4}>
                            <FormField
                                name="prosperFlatExtra"
                                label="Flat Extra"
                                formFieldType={FormFieldType.CURRENCY}
                                parameter={parameters.prosperFlatExtra}
                                errors={!!errors.prosperFlatExtra}
                                helperText={errors.prosperFlatExtra?.message}
                                valueChanged={valueChanged}
                                required
                                disabled
                            />
                        </Grid>

                        {parameters.prosperFlatExtra.value > 0 && (
                            <Grid item xs={4}>
                                <FormField
                                    name="prosperFlatExtraDuration"
                                    label="Flat Extra Duration (years)"
                                    formFieldType={FormFieldType.TEXT}
                                    parameter={parameters.prosperFlatExtraDuration}
                                    errors={!!errors.prosperFlatExtraDuration}
                                    helperText={errors.prosperFlatExtraDuration?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <CategoryHeader name="Benefit Schedule" number={4} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperFaceAmountType"
                                label="Face Amount Type"
                                formFieldType={FormFieldType.SELECT}
                                options={['Specify', 'Solve', 'Target', 'Guideline', 'Seven Pay']}
                                optionValues={['Specify', 'Solve', 'Target', 'Guideline', 'SevenPay']}
                                parameter={parameters.prosperFaceAmountType}
                                errors={!!errors.prosperFaceAmountType}
                                helperText={errors.prosperFaceAmountType?.message}
                                valueChanged={valueChanged}
                                required
                                disabled={watchAmplifySolves === 'OptimalSolve'}
                            />
                        </Grid>

                        {['Specify'].includes(parameters.prosperFaceAmountType.value) && (
                            <Grid item xs={4}>
                                <FormField
                                    name="prosperFaceAmount"
                                    label="Face Amount"
                                    formFieldType={FormFieldType.CURRENCY}
                                    parameter={parameters.prosperFaceAmount}
                                    errors={!!errors.prosperFaceAmount}
                                    helperText={errors.prosperFaceAmount?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                        )}
                        <Grid item xs={4}>
                            <FormField
                                name="prosperDeathBenefitOption"
                                label="Death Benefit Option"
                                formFieldType={FormFieldType.SELECT}
                                options={['A - Level', 'B - Increasing']}
                                optionValues={['ALevel', 'BIncreasing']}
                                parameter={parameters.prosperDeathBenefitOption}
                                errors={!!errors.prosperDeathBenefitOption}
                                helperText={errors.prosperDeathBenefitOption?.message}
                                valueChanged={valueChanged}
                                required
                                disabled={watchAmplifySolves === 'OptimalSolve'}
                            />
                        </Grid>
                        {parameters.prosperDeathBenefitOption.value === 'BIncreasing' && (
                            <Grid item xs={4}>
                                <FormField
                                    name="prosperDeathBenefitChangeAge"
                                    label="Death Benefit Change Age"
                                    formFieldType={FormFieldType.NUMBER}
                                    parameter={parameters.prosperDeathBenefitChangeAge}
                                    errors={!!errors.prosperDeathBenefitChangeAge}
                                    helperText={errors.prosperDeathBenefitChangeAge?.message}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <CategoryHeader name="Premium Schedule" number={5} />
                        </Grid>
                        {watchAmplifySolves !== 'OptimalSolve' && (
                            <>
                                <Grid item xs={4}>
                                    <FormField
                                        name="prosperPremiumType"
                                        label="Premium Type"
                                        formFieldType={FormFieldType.SELECT}
                                        options={['Specify', 'Solve', 'Target', 'Guideline', 'Seven Pay']}
                                        optionValues={['Specify', 'Solve', 'Target', 'Guideline', 'SevenPay']}
                                        parameter={parameters.prosperPremiumType}
                                        errors={!!errors.prosperPremiumType}
                                        helperText={errors.prosperPremiumType?.message}
                                        valueChanged={valueChanged}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <FormField
                                        name="prosperPremium"
                                        label="Premium"
                                        formFieldType={FormFieldType.CURRENCY}
                                        parameter={parameters.prosperPremium}
                                        errors={!!errors.prosperPremium}
                                        helperText={errors.prosperPremium?.message}
                                        valueChanged={valueChanged}
                                        required
                                    />
                                    {showPremiumWarningPremium && <WarningMessage value={premiumThresholdPremium} />}
                                </Grid>
                            </>
                        )}
                        <Grid item xs={4}>
                            <FormField
                                name="prosperPremiumMode"
                                label="Premium Mode"
                                formFieldType={FormFieldType.SELECT}
                                options={['Monthly', 'Quarterly', 'SemiAnnually', 'Annually']}
                                optionValues={['Monthly', 'Quarterly', 'SemiAnnually', 'Annually']}
                                parameter={parameters.prosperPremiumMode}
                                errors={!!errors.prosperPremiumMode}
                                helperText={errors.prosperPremiumMode?.message}
                                valueChanged={valueChanged}
                                required
                                disabled={watchAmplifySolves === 'OptimalSolve'}
                            />
                        </Grid>
                        {['vivianh@getamplifylife.com'].includes(User.Info.email) && (
                            <Grid item xs={4}>
                                <FormField
                                    name="prosperLumpSum"
                                    label="Lump Sum"
                                    formFieldType={FormFieldType.CURRENCY}
                                    parameter={parameters.prosperLumpSum}
                                    errors={!!errors.prosperLumpSum}
                                    helperText={errors.prosperLumpSum?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                        )}
                        <Grid item xs={4}>
                            <FormField
                                name="prosperPremiumEndYear"
                                label="Premium End Year"
                                formFieldType={FormFieldType.TEXT}
                                parameter={parameters.prosperPremiumEndYear}
                                errors={!!errors.prosperPremiumEndYear}
                                helperText={errors.prosperPremiumEndYear?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperPremiumAge"
                                label="Premium Age"
                                formFieldType={FormFieldType.TEXT}
                                parameter={parameters.prosperPremiumAge}
                                errors={!!errors.prosperPremiumAge}
                                helperText={errors.prosperPremiumAge?.message}
                                valueChanged={valueChanged}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CategoryHeader name="Policy Credits: Cash Value Index Allocation" number={6} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperIndexName"
                                label="Index Name"
                                formFieldType={FormFieldType.TEXT}
                                parameter={parameters.prosperIndexName}
                                errors={!!errors.prosperIndexName}
                                helperText={errors.prosperIndexName?.message}
                                valueChanged={valueChanged}
                                required
                                disabled
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperIndexedAllocationPercent"
                                label="Indexed Allocation %"
                                formFieldType={FormFieldType.PERCENTAGE}
                                parameter={parameters.prosperIndexedAllocationPercent}
                                errors={!!errors.prosperIndexedAllocationPercent}
                                helperText={errors.prosperIndexedAllocationPercent?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperIndexedAccountRateOfReturn"
                                label="Indexed Account Rate of Return"
                                formFieldType={FormFieldType.PERCENTAGE}
                                parameter={parameters.prosperIndexedAccountRateOfReturn}
                                errors={!!errors.prosperIndexedAccountRateOfReturn}
                                helperText={errors.prosperIndexedAccountRateOfReturn?.message}
                                valueChanged={valueChanged}
                                precise
                                required
                            />
                        </Grid>
                        {/* <Grid item xs={12}>
                            <CategoryHeader name="Policy Distributions" number={7} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperDistributionSolveType"
                                label="Distribution Solve Type"
                                formFieldType={FormFieldType.SELECT}
                                options={['Specify', 'Solve']}
                                optionValues={['Specify', 'Solve']}
                                parameter={parameters.prosperDistributionSolveType}
                                errors={!!errors.prosperDistributionSolveType}
                                helperText={errors.prosperDistributionSolveType?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>

                        {parameters.prosperDistributionSolveType.value === 'Specify' && (
                            <Grid item xs={4}>
                                <FormField
                                    name="prosperDistributionAmount"
                                    label="Monthly Distribution Amount"
                                    formFieldType={FormFieldType.CURRENCY}
                                    parameter={parameters.prosperDistributionAmount}
                                    errors={!!errors.prosperDistributionAmount}
                                    helperText={errors.prosperDistributionAmount?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                        )}
                        <Grid item xs={4}>
                            <FormField
                                name="prosperDistributionType"
                                label="Distribution Type"
                                formFieldType={FormFieldType.SELECT}
                                options={['Indexed Loan', 'Withdrawal', 'Withdrawal to Basis then Loan']}
                                optionValues={['IndexedLoan', 'Withdrawal', 'WithdrawaltoBasisthenLoan']}
                                parameter={parameters.prosperDistributionType}
                                errors={!!errors.prosperDistributionType}
                                helperText={errors.prosperDistributionType?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>
                        <>
                            <Grid item xs={2}>
                                <FormField
                                    name="prosperDistributionStartAge"
                                    label="Start Age"
                                    formFieldType={FormFieldType.NUMBER}
                                    parameter={parameters.prosperDistributionStartAge}
                                    errors={!!errors.prosperDistributionStartAge}
                                    helperText={errors.prosperDistributionStartAge?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <FormField
                                    name="prosperDistributionEndAge"
                                    label="End Age"
                                    formFieldType={FormFieldType.NUMBER}
                                    parameter={parameters.prosperDistributionEndAge}
                                    errors={!!errors.prosperDistributionEndAge}
                                    helperText={errors.prosperDistributionEndAge?.message}
                                    valueChanged={valueChanged}
                                    required
                                />
                            </Grid>
                        </> */}

                        <Grid item xs={12}>
                            <CategoryHeader name="Tax Compliance" number={7} />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperAllowMEC"
                                label="Allow MEC"
                                formFieldType={FormFieldType.SWITCH}
                                parameter={parameters.prosperAllowMEC}
                                errors={!!errors.prosperAllowMEC}
                                helperText={errors.prosperAllowMEC?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormField
                                name="prosperDefinitionOfLifeInsurance"
                                label="Definition of Life Insurance"
                                formFieldType={FormFieldType.SELECT}
                                options={['CVAT', 'GPT']}
                                optionValues={['CVAT', 'GPT']}
                                parameter={parameters.prosperDefinitionOfLifeInsurance}
                                errors={!!errors.prosperDefinitionOfLifeInsurance}
                                helperText={errors.prosperDefinitionOfLifeInsurance?.message}
                                valueChanged={valueChanged}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CategoryHeader name="Solve" number={8} />
                        </Grid>
                        {(parameters.prosperPremiumType.value === 'Solve' ||
                            parameters.prosperFaceAmountType.value === 'Solve') &&
                            watchAmplifySolves !== 'OptimalSolve' && (
                                <Grid item xs={4}>
                                    <FormField
                                        name="prosperSolveGoal"
                                        label="Solve Goal"
                                        formFieldType={FormFieldType.CURRENCY}
                                        parameter={parameters.prosperSolveGoal}
                                        errors={!!errors.prosperSolveGoal}
                                        helperText={errors.prosperSolveGoal?.message}
                                        valueChanged={valueChanged}
                                        required
                                    />
                                </Grid>
                            )}
                        <Grid item xs={4}>
                            <FormField
                                name="prosperSolveAge"
                                label="Solve Age"
                                formFieldType={FormFieldType.NUMBER}
                                parameter={parameters.prosperSolveAge}
                                errors={!!errors.prosperSolveAge}
                                helperText={errors.prosperSolveAge?.message}
                                valueChanged={valueChanged}
                                required
                                disabled={watchAmplifySolves === 'OptimalSolve'}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
});

export default ProsperTab;
